/**
 * Site-wide add-to-cart detection for the holdout analysis (CONTRACTS §J.5 `add_to_cart_rate`): the same signal must
 * be measured in **both** arms, so it cannot come from the chat panel. Detects Shopify AJAX/forms (`/cart/add`),
 * WooCommerce Store API (`/wc/store/v1/cart/add-item`), `?wc-ajax=add_to_cart` and classic `add-to-cart` forms.
 * Calls `onAdd` at most once per page view.
 */
const ADD_URL = [/\/cart\/add(\.js|\.json)?(\?|$)/, /\/wc\/store(\/v\d+)?\/cart\/add-item/, /[?&]wc-ajax=add_to_cart(&|$)/];

export function isAddToCartUrl(url: string): boolean {
  return ADD_URL.some((re) => re.test(url));
}

function urlOf(input: unknown): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input === 'object' && 'url' in input) return String((input as { url: unknown }).url);
  return '';
}

export function trackSiteAddToCart(onAdd: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    try {
      onAdd();
    } catch {
      /* never break the store */
    }
  };

  const origFetch = window.fetch;
  let wrappedFetch: typeof fetch | null = null;
  if (typeof origFetch === 'function') {
    wrappedFetch = function (this: unknown, input: RequestInfo | URL, init?: RequestInit) {
      const p = origFetch.call(this ?? window, input, init);
      try {
        const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
        if (method === 'POST' && isAddToCartUrl(urlOf(input))) void p.then((r) => r.ok && fire(), () => undefined);
      } catch {
        /* ignore */
      }
      return p;
    } as typeof fetch;
    window.fetch = wrappedFetch;
  }

  const xhrProto = typeof XMLHttpRequest !== 'undefined' ? XMLHttpRequest.prototype : null;
  const origOpen = xhrProto?.open;
  if (xhrProto && origOpen) {
    xhrProto.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
      try {
        if (String(method).toUpperCase() === 'POST' && isAddToCartUrl(urlOf(url))) {
          this.addEventListener('load', () => this.status >= 200 && this.status < 300 && fire(), { once: true });
        }
      } catch {
        /* ignore */
      }
      return (origOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
    } as typeof xhrProto.open;
  }

  const onSubmit = (e: Event) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') ?? '';
    if (isAddToCartUrl(action) || form.querySelector('[name="add-to-cart"]')) fire();
  };
  document.addEventListener('submit', onSubmit, true);

  return () => {
    if (wrappedFetch && window.fetch === wrappedFetch) window.fetch = origFetch;
    if (xhrProto && origOpen) xhrProto.open = origOpen;
    document.removeEventListener('submit', onSubmit, true);
  };
}
